import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../style.css';

const API       = 'https://culturlingua.com/blog_api.php';
const WA_NUMBER = '523333449139'; // ← cambia aquí si cambia el número
const WA_MSG    = encodeURIComponent('Hola, leí un artículo del blog y me gustaría información sobre los cursos de inglés.');

/* ── Autor global del sitio ── */
const SITE_AUTHOR = {
  name:     'Culturlingua Academy',
  role:     'Equipo editorial',
  initials: 'CL',
  bio:      'Escuela de idiomas en Tlaquepaque, Jalisco. Más de 10 años formando estudiantes con maestros certificados y grupos pequeños.',
  links: [
    { label: '📍 Ver ubicación', href: '/OurLocation' },
    { label: '📚 Más artículos', href: '/blog' },
    { label: '💬 Contactar',    href: '/contacto' },
  ],
};

/* ── Parse FAQs from post.faq_json ── */
function parseFaqs(faqJson) {
  if (!faqJson) return [];
  try {
    const arr = typeof faqJson === 'string' ? JSON.parse(faqJson) : faqJson;
    return Array.isArray(arr) ? arr.filter(f => f.q && f.a) : [];
  } catch { return []; }
}

/* ── Build FAQ JSON-LD schema ── */
function buildFaqSchema(faqs, postUrl) {
  if (!faqs.length) return null;
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  });
}

/* Safari-safe hex darkening — replaces color-mix() */
function darkenHex(hex, amount = 0.35) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8)  & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round(( num        & 0xff) * (1 - amount)));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function formatDate(str) {
  return new Date(str).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* Reading-progress bar */
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div style={{ position:'fixed', top:'72px', left:0, right:0, height:'3px',
      background:'rgba(255,255,255,.06)', zIndex:100, pointerEvents:'none' }}>
      <div style={{ height:'100%', width:`${pct}%`,
        background:'linear-gradient(90deg, var(--blue), var(--gold))',
        transition:'width .1s linear' }} />
    </div>
  );
}

/* Returns colors and pattern for the post hero — used for background only */
function HeroVisual({ color, category }) {
  const c1 = color || '#1a1744';
  const c2 = darkenHex(c1, 0.45);
  return { c1, c2 };
}

const S = `
@keyframes spin    { to { transform: rotate(360deg) } }
@keyframes fadeUp  { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:none } }
@keyframes shimmer { from { background-position:-200% 0 } to { background-position:200% 0 } }

/* ── Hero ── */
.bp-hero { padding:64px 48px 0; position:relative; overflow:hidden; }
.bp-hero-inner { max-width:760px; margin:0 auto; position:relative; z-index:2; animation:fadeUp .6s ease both; }
.bp-breadcrumb { display:flex; align-items:center; gap:10px; margin-bottom:32px; flex-wrap:wrap; }
.bp-back { color:rgba(255,255,255,.45); font-size:.85rem; text-decoration:none; transition:color .2s; display:inline-flex; align-items:center; gap:6px; }
.bp-back:hover { color:rgba(255,255,255,.8); }
.bp-sep { color:rgba(255,255,255,.16); }
.bp-cat-pill { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; padding:4px 12px; border-radius:100px; }

.bp-title { font-family:'Fraunces',serif; font-size:clamp(1.8rem,4vw,3rem); font-weight:900; color:white; line-height:1.12; letter-spacing:-.5px; margin-bottom:20px; }
.bp-excerpt { color:rgba(255,255,255,.6); font-size:1.05rem; line-height:1.78; margin-bottom:32px; }
.bp-meta { display:flex; gap:8px; flex-wrap:wrap; align-items:center; padding-bottom:40px; }
.bp-meta-item { color:rgba(255,255,255,.38); font-size:.8rem; display:inline-flex; align-items:center; gap:4px; }
.bp-meta-dot { color:rgba(255,255,255,.14); font-size:.6rem; }

/* White wave connector from hero to body */
.bp-wave { display:block; width:100%; height:48px; position:relative; }

/* ── Body ── */
.bp-body { background:var(--cream); padding:0 48px 80px; }
.bp-body-inner { max-width:760px; margin:0 auto; padding-top:52px; }

/* ── Article content ── */
.blog-content { font-family:'DM Sans',sans-serif; color:#2d2d2d; line-height:1.9; font-size:1.06rem; }
.blog-content > *:first-child { margin-top:0; }
.blog-content h2 { font-family:'Fraunces',serif; font-size:1.65rem; font-weight:900; color:var(--navy); margin:56px 0 18px; line-height:1.2; letter-spacing:-.35px; }
.blog-content h2::before { content:''; display:block; width:36px; height:3px; background:var(--gold); border-radius:2px; margin-bottom:12px; }
.blog-content h3 { font-family:'Fraunces',serif; font-size:1.2rem; font-weight:800; color:var(--navy); margin:36px 0 12px; }
.blog-content p { margin-bottom:24px; }
.blog-content ul,.blog-content ol { padding-left:20px; margin-bottom:24px; }
.blog-content li { margin-bottom:10px; padding-left:4px; }
.blog-content ul li::marker { color:var(--gold); }
.blog-content ol li::marker { color:var(--blue); font-weight:700; }
.blog-content strong { color:var(--navy); font-weight:700; }
.blog-content em { font-style:italic; color:#555; }
.blog-content a { color:var(--blue); font-weight:600; text-decoration:none; border-bottom:1.5px solid rgba(59,111,224,.25); transition:border-color .2s,color .2s; }
.blog-content a:hover { border-bottom-color:var(--blue); }
.blog-content blockquote {
  border-left:none;
  padding:28px 32px;
  margin:36px 0;
  background:linear-gradient(135deg,rgba(245,200,66,.07) 0%,rgba(245,200,66,.03) 100%);
  border-radius:16px;
  font-style:italic;
  color:#555;
  font-size:1.08rem;
  position:relative;
  border:1px solid rgba(245,200,66,.18);
}
.blog-content blockquote::before {
  content:'"';
  position:absolute; top:-4px; left:20px;
  font-family:'Fraunces',serif; font-size:4rem; font-weight:900;
  color:var(--gold); line-height:1; opacity:.6;
}
.blog-content img { max-width:100%; border-radius:14px; margin:8px 0; display:block; }
.blog-content hr { border:none; height:1px; background:linear-gradient(90deg,transparent,#e0dbd0,transparent); margin:48px 0; }
.blog-content code { background:rgba(26,23,68,.06); color:var(--navy); padding:2px 8px; border-radius:6px; font-size:.88em; font-family:'Courier New',monospace; }
.blog-content pre { background:var(--navy); color:#c8d8ff; padding:28px; border-radius:14px; overflow-x:auto; margin:28px 0; }
.blog-content pre code { background:transparent; color:inherit; padding:0; }
.blog-content table { width:100%; border-collapse:collapse; margin:28px 0; border-radius:12px; overflow:hidden; }
.blog-content th { background:var(--navy); color:white; padding:12px 16px; text-align:left; font-size:.85rem; font-weight:700; }
.blog-content td { padding:11px 16px; border-bottom:1px solid #ede9e0; font-size:.93rem; }
.blog-content tr:last-child td { border-bottom:none; }
.blog-content tr:nth-child(even) td { background:rgba(245,200,66,.04); }

/* ── Share strip ── */
.bp-share { display:flex; align-items:center; gap:12px; padding:20px 24px; background:white; border-radius:14px; margin-bottom:36px; box-shadow:0 2px 12px rgba(26,23,68,.06); }
.bp-share-lbl { font-size:.75rem; font-weight:800; text-transform:uppercase; letter-spacing:.1em; color:#bbb; margin-right:4px; }
.bp-share-btn { padding:7px 16px; border-radius:100px; border:1.5px solid #e8e2d8; background:white; color:#555; font-family:'DM Sans',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .2s; text-decoration:none; display:inline-flex; align-items:center; gap:6px; }
.bp-share-btn:hover { border-color:var(--navy); color:var(--navy); transform:translateY(-1px); }
.bp-share-wa  { color:#16a34a; border-color:#bbf7d0; background:#f0fdf4; }
.bp-share-wa:hover  { border-color:#16a34a!important; color:#15803d!important; background:#dcfce7; }
.bp-share-li  { color:#0a66c2; border-color:#bfdbfe; background:#eff6ff; }
.bp-share-li:hover  { border-color:#0a66c2!important; color:#0a66c2!important; background:#dbeafe; }
.bp-share-fb  { color:#1877f2; border-color:#bfdbfe; background:#eff6ff; }
.bp-share-fb:hover  { border-color:#1877f2!important; color:#1877f2!important; background:#dbeafe; }
.bp-share-mail { color:#6b7280; border-color:#e5e7eb; background:#f9fafb; }
.bp-share-mail:hover { border-color:#374151!important; color:#374151!important; background:#f3f4f6; }

/* ── TABLE OF CONTENTS ── */
.bp-toc { background:white; border:1px solid #ede9e0; border-radius:16px; padding:22px 26px; margin-bottom:36px; position:relative; }
.bp-toc::before { content:''; position:absolute; left:0; top:16px; bottom:16px; width:3px; background:linear-gradient(180deg,var(--blue),var(--gold)); border-radius:0 2px 2px 0; }
.bp-toc-label { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.12em; color:#bbb; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
.bp-toc-label::after { content:''; flex:1; height:1px; background:#ede9e0; }
.bp-toc-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:2px; }
.bp-toc-list li a { color:#555; text-decoration:none; font-size:.88rem; line-height:1.5; display:flex; align-items:baseline; gap:10px; padding:5px 8px; border-radius:6px; transition:all .18s; }
.bp-toc-list li a:hover { color:var(--navy); background:rgba(26,23,68,.04); }
.bp-toc-list li a .toc-num { font-size:.7rem; font-weight:800; color:var(--blue); opacity:.55; flex-shrink:0; min-width:14px; }
.bp-toc-list li a.toc-active { color:var(--navy); font-weight:600; background:rgba(59,111,224,.05); }
.bp-toc-list li a.toc-active .toc-num { opacity:1; }
.bp-read-track { margin-top:14px; padding-top:14px; border-top:1px solid #f0ebe2; display:flex; align-items:center; gap:10px; }
.bp-read-track-bar { flex:1; height:4px; background:#ede9e0; border-radius:2px; overflow:hidden; }
.bp-read-track-fill { height:100%; width:0%; background:linear-gradient(90deg,var(--blue),var(--gold)); border-radius:2px; transition:width .3s ease; }
.bp-read-track-label { font-size:.72rem; color:#bbb; white-space:nowrap; min-width:100px; text-align:right; }

/* ── CTA box ── */
.bp-cta { border-radius:24px; margin-top:64px; overflow:hidden; position:relative; }
.bp-cta-top { background:var(--navy); padding:44px 44px 36px; text-align:center; position:relative; overflow:hidden; }
.bp-cta-orb { position:absolute; top:-80px; right:-80px; width:280px; height:280px; background:radial-gradient(circle,rgba(245,200,66,.13) 0%,transparent 70%); pointer-events:none; }
.bp-cta-orb2 { position:absolute; bottom:-60px; left:-60px; width:200px; height:200px; background:radial-gradient(circle,rgba(59,111,224,.13) 0%,transparent 70%); pointer-events:none; }
.bp-cta-badge { display:inline-flex; align-items:center; gap:6px; background:rgba(245,200,66,.12); border:1px solid rgba(245,200,66,.28); border-radius:100px; padding:5px 14px; font-size:.72rem; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:var(--gold); margin-bottom:18px; }
.bp-cta h3 { font-family:'Fraunces',serif; color:white; font-size:1.55rem; font-weight:900; margin-bottom:10px; letter-spacing:-.3px; line-height:1.2; }
.bp-cta-sub { color:rgba(255,255,255,.55); margin-bottom:0; line-height:1.65; font-size:.97rem; max-width:380px; margin-left:auto; margin-right:auto; }
.bp-cta-bottom { background:#f5f0e8; padding:20px 44px 28px; }
.bp-cta-perks { display:flex; justify-content:center; gap:6px; flex-wrap:wrap; margin-bottom:24px; margin-top:0; }
.bp-cta-perk { display:inline-flex; align-items:center; gap:5px; font-size:.78rem; font-weight:600; color:#555; background:white; padding:5px 12px; border-radius:100px; border:1px solid #e5dfd5; white-space:nowrap; }
.bp-cta-perk svg { flex-shrink:0; }
.bp-cta-btns { display:flex; gap:10px; justify-content:center; flex-wrap:wrap; }
.bp-cta-social { margin-top:16px; text-align:center; }
.bp-cta-social-line { font-size:.75rem; color:#aaa; display:flex; align-items:center; justify-content:center; gap:8px; }
.bp-cta-social-line::before,.bp-cta-social-line::after { content:''; flex:1; max-width:60px; height:1px; background:#ddd; }
.bp-cta-avatars { display:inline-flex; margin-right:6px; }
.bp-cta-avatar { width:22px; height:22px; border-radius:50%; border:2px solid #f5f0e8; background:var(--navy); color:white; font-size:.55rem; font-weight:800; display:flex; align-items:center; justify-content:center; margin-left:-6px; }
.bp-cta-avatar:first-child { margin-left:0; }

/* ── Related ── */
.bp-related { margin-top:56px; }
.bp-rel-lbl { font-size:.68rem; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#c0b8a8; margin-bottom:22px; display:flex; align-items:center; gap:12px; }
.bp-rel-lbl::after { content:''; flex:1; height:1px; background:#e8e2d8; }
.bp-rel-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
.bp-rel-card { text-decoration:none; background:white; border-radius:16px; overflow:hidden; box-shadow:0 2px 12px rgba(26,23,68,.06); transition:transform .22s ease,box-shadow .22s ease; display:flex; flex-direction:column; }
.bp-rel-card:hover { transform:translateY(-4px); box-shadow:0 12px 36px rgba(26,23,68,.12); }
.bp-rel-visual { height:88px; position:relative; overflow:hidden; background:white; }
.bp-rel-body { padding:18px 20px 16px; flex:1; }
.bp-rel-title { font-family:'Fraunces',serif; color:var(--navy); font-weight:800; line-height:1.3; margin-bottom:10px; font-size:.98rem; }
.bp-rel-cta { color:var(--blue); font-size:.82rem; font-weight:700; display:inline-flex; align-items:center; gap:4px; transition:gap .18s; }
.bp-rel-card:hover .bp-rel-cta { gap:8px; }

/* ── Fullscreen states ── */
.bp-fullscreen { min-height:60vh; display:flex; align-items:center; justify-content:center; background:var(--cream); }

/* ── Inline mid-article CTA ── */
.bp-inline-cta { background:linear-gradient(135deg,var(--navy) 0%,#26225d 100%); border-radius:18px; padding:28px 32px; margin:44px 0; position:relative; overflow:hidden; display:flex; align-items:center; gap:24px; }
.bp-inline-cta::before { content:''; position:absolute; top:-40px; right:-40px; width:160px; height:160px; background:radial-gradient(circle,rgba(245,200,66,.1),transparent 70%); pointer-events:none; }
.bp-inline-cta-text h4 { font-family:'Fraunces',serif; color:white; font-size:1.1rem; font-weight:900; margin-bottom:5px; }
.bp-inline-cta-text p  { color:rgba(255,255,255,.55); font-size:.86rem; margin:0; line-height:1.55; }
.bp-inline-cta a { flex-shrink:0; background:var(--gold); color:var(--navy); padding:11px 20px; border-radius:8px; font-weight:700; font-size:.86rem; text-decoration:none; white-space:nowrap; transition:all .2s; box-shadow:0 4px 16px rgba(245,200,66,.3); }
.bp-inline-cta a:hover { background:var(--gold-light); transform:translateY(-1px); }
@media(max-width:600px) { .bp-inline-cta { flex-direction:column; gap:14px; } .bp-inline-cta a { width:100%; text-align:center; } }

/* ── Exit intent popup ── */
@keyframes ei-popIn   { from { opacity:0; transform:translate(-50%,-48%) scale(.94); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }
@keyframes ei-fadeInBg { from { opacity:0; } to { opacity:1; } }
.ei-overlay { position:fixed; inset:0; background:rgba(10,8,40,.72); z-index:9999; display:none; backdrop-filter:blur(3px); animation:ei-fadeInBg .25s ease; align-items:center; justify-content:center; }
.ei-overlay.show { display:flex; }
.ei-modal { position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); z-index:10000; width:min(460px,92vw); background:white; border-radius:24px; overflow:hidden; animation:ei-popIn .3s cubic-bezier(.34,1.56,.64,1) both; box-shadow:0 32px 80px rgba(10,8,40,.4); }
.ei-top { background:var(--navy); padding:36px 36px 28px; text-align:center; position:relative; overflow:hidden; }
.ei-top::before { content:''; position:absolute; top:-60px; right:-60px; width:200px; height:200px; background:radial-gradient(circle,rgba(245,200,66,.12),transparent 70%); pointer-events:none; }
.ei-top::after  { content:''; position:absolute; bottom:-40px; left:-40px; width:160px; height:160px; background:radial-gradient(circle,rgba(59,111,224,.1),transparent 70%); pointer-events:none; }
.ei-close { position:absolute; top:12px; right:14px; background:rgba(255,255,255,.1); border:none; color:rgba(255,255,255,.5); width:28px; height:28px; border-radius:50%; cursor:pointer; font-size:1.1rem; line-height:1; display:flex; align-items:center; justify-content:center; transition:all .2s; z-index:2; font-family:sans-serif; }
.ei-close:hover { background:rgba(255,255,255,.22); color:white; }
.ei-emoji { font-size:2.6rem; margin-bottom:12px; display:block; line-height:1; position:relative; z-index:1; }
.ei-title { font-family:'Fraunces',serif; color:white; font-size:1.35rem; font-weight:900; line-height:1.2; margin-bottom:8px; position:relative; z-index:1; }
.ei-sub   { color:rgba(255,255,255,.52); font-size:.88rem; line-height:1.6; position:relative; z-index:1; margin:0; }
.ei-bottom { padding:22px 32px 26px; background:#faf8f3; display:flex; flex-direction:column; gap:10px; }
.ei-dismiss { background:none; border:none; font-size:.78rem; color:#aaa; cursor:pointer; font-family:'DM Sans',sans-serif; transition:color .2s; text-align:center; padding:4px; }
.ei-dismiss:hover { color:#666; }

@media (max-width:860px) {
  .bp-hero,.bp-body { padding-left:20px; padding-right:20px; }
  .bp-cta-top { padding:36px 24px 28px; }
  .bp-cta-bottom { padding:18px 24px 24px; }
}
@media (max-width:560px) {
  .bp-rel-grid { grid-template-columns:1fr; }
  .bp-share { flex-wrap:wrap; }
}
`;

function RelVisual({ color, icon, category }) {
  const c1 = color || '#3b6fe0';
  const darken = darkenHex(c1, 0.28);
  const initials = (category || 'Ge').substring(0, 2).toUpperCase();
  return (
    <div className="bp-rel-visual" style={{background:'white', position:'relative'}}>
      {/* Accent strip */}
      <div style={{position:'absolute',top:0,left:0,right:0,height:'4px',
        background:`linear-gradient(90deg,${c1},${darken})`}}/>
      {/* Emoji pill */}
      <div style={{
        display:'flex', alignItems:'center', gap:'10px',
        padding:'16px 18px 0', position:'relative', zIndex:2,
      }}>
        <div style={{
          width:'40px', height:'40px', borderRadius:'10px',
          background:`${c1}14`, display:'flex', alignItems:'center',
          justifyContent:'center', fontSize:'20px', lineHeight:1, flexShrink:0,
        }}>
          {icon || <span style={{fontFamily:"'Fraunces',serif",fontSize:'1rem',fontWeight:900,color:c1}}>{initials}</span>}
        </div>
        <span style={{fontSize:'10px',fontWeight:800,letterSpacing:'.07em',
          textTransform:'uppercase',color:c1}}>{category}</span>
      </div>
    </div>
  );
}

/* ── Tracking helpers (module-level, not recreated on render) ── */
function getSessionId() {
  let sid = sessionStorage.getItem('cl_sid');
  if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('cl_sid', sid); }
  return sid;
}
function getDevice() {
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  return 'desktop';
}
function getUTMs() {
  const p = new URLSearchParams(window.location.search);
  return { utm_source: p.get('utm_source')||'', utm_medium: p.get('utm_medium')||'', utm_campaign: p.get('utm_campaign')||'' };
}

// A/B variant: deterministic per session, stored so it doesn't change on refresh
function getABVariant() {
  let v = sessionStorage.getItem('cl_ab');
  if (!v) { v = Math.random() < 0.5 ? 'A' : 'B'; sessionStorage.setItem('cl_ab', v); }
  return v;
}

// Simple click-position heatmap stored in sessionStorage
// Format: [{x: pct, y: pct, t: timestamp}, ...]
function recordHeatmapClick(e) {
  try {
    const article = document.querySelector('.blog-content');
    if (!article) return;
    const rect = article.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top + window.scrollY - rect.top) / (article.scrollHeight || 1)) * 100);
    const key = 'cl_heatmap';
    const existing = JSON.parse(sessionStorage.getItem(key) || '[]');
    existing.push({ x, y, t: Date.now() });
    // Keep last 200 points
    if (existing.length > 200) existing.splice(0, existing.length - 200);
    sessionStorage.setItem(key, JSON.stringify(existing));
  } catch(err) {}
}

/* ── Sticky CTA bar for mobile — appears after 40% scroll ── */
/* ── Inline mid-article CTA (auto-inserted after ~40% of content) ── */
function InlineCta({ examUrl, category }) {
  const copies = {
    'Certificaciones': { title: '¿Sabes qué certificación necesitas?', sub: 'Haz el examen de ubicación y te decimos qué camino tomar para lograrlo.' },
    'default':         { title: '¿En qué nivel estás realmente?',      sub: 'Examen gratis, 10 minutos. Sin registro ni tarjeta.' },
  };
  const c = copies[category] || copies['default'];
  return (
    <div className="bp-inline-cta">
      <div className="bp-inline-cta-text">
        <h4>{c.title}</h4>
        <p>{c.sub}</p>
      </div>
      <Link to={examUrl} className="btn-glow" style={{flexShrink:0,fontSize:'.86rem',padding:'11px 20px'}} onClick={() => {}}>
        Hacer examen →
      </Link>
    </div>
  );
}

/* ── Table of Contents ── */
function TableOfContents({ content, readTime }) {
  const [activeId, setActiveId]   = React.useState('');
  const [pct,      setPct]        = React.useState(0);

  // Parse h2 headings from content HTML
  const headings = React.useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = content || '';
    return Array.from(div.querySelectorAll('h2')).map((el, i) => ({
      id:   el.id || `h2-${i}`,
      text: el.textContent.trim(),
      num:  String(i + 1),
    }));
  }, [content]);

  // Inject IDs into rendered h2s (content is dangerouslySetInnerHTML)
  React.useEffect(() => {
    const article = document.querySelector('.blog-content');
    if (!article) return;
    article.querySelectorAll('h2').forEach((el, i) => {
      if (!el.id) el.id = `h2-${i}`;
    });
  }, [content]);

  // Active section + reading progress on scroll
  React.useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      const p     = total > 0 ? Math.round((el.scrollTop / total) * 100) : 0;
      setPct(p);
      const scrollY = window.scrollY + 130;
      let active = headings[0]?.id || '';
      headings.forEach(h => {
        const node = document.getElementById(h.id);
        if (node && node.offsetTop <= scrollY) active = h.id;
      });
      setActiveId(active);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [headings]);

  if (headings.length < 2) return null;

  const mins    = parseInt(readTime) || 5;
  const remaining = Math.max(0, Math.round(mins * (1 - pct / 100)));
  const label   = remaining === 0 ? 'Completado ✓' : `${remaining} min restante${remaining !== 1 ? 's' : ''}`;

  return (
    <nav className="bp-toc">
      <div className="bp-toc-label">Contenido del artículo</div>
      <ul className="bp-toc-list">
        {headings.map(h => (
          <li key={h.id}>
            <a href={`#${h.id}`} className={activeId === h.id ? 'toc-active' : ''}
              onClick={e => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
              <span className="toc-num">{h.num}</span>
              {h.text.replace(/^\d+\.\s*/, '')}
            </a>
          </li>
        ))}
      </ul>
      <div className="bp-read-track">
        <div className="bp-read-track-bar">
          <div className="bp-read-track-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="bp-read-track-label">{label}</span>
      </div>
    </nav>
  );
}

/* ── Author bio (uses global SITE_AUTHOR constant) ── */
function AuthorBio() {
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:'20px',background:'white',borderRadius:'18px',padding:'24px 28px',marginTop:'52px',boxShadow:'0 2px 14px rgba(26,23,68,.06)',border:'1px solid #ede9e0'}}>
      <div style={{width:'64px',height:'64px',borderRadius:'50%',flexShrink:0,background:'linear-gradient(135deg,var(--navy),var(--blue))',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Fraunces',serif",fontSize:'1.4rem',fontWeight:900,color:'white',border:'3px solid var(--gold)',letterSpacing:'-1px'}}>
        {SITE_AUTHOR.initials}
      </div>
      <div>
        <div style={{fontSize:'.68rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.1em',color:'var(--blue)',marginBottom:'3px'}}>{SITE_AUTHOR.role}</div>
        <div style={{fontFamily:"'Fraunces',serif",fontSize:'1.05rem',fontWeight:900,color:'var(--navy)',marginBottom:'6px'}}>{SITE_AUTHOR.name}</div>
        <p style={{fontSize:'.87rem',color:'#666',lineHeight:1.65,marginBottom:'10px'}}>{SITE_AUTHOR.bio}</p>
        <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
          {SITE_AUTHOR.links.map(l => (
            <Link key={l.href} to={l.href} style={{fontSize:'.75rem',fontWeight:600,color:'var(--blue)',textDecoration:'none',padding:'4px 10px',borderRadius:'100px',border:'1px solid rgba(59,111,224,.2)',background:'rgba(59,111,224,.04)',transition:'all .2s'}}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── FAQ accordion (reads from post.faq_json) ── */
function FaqAccordion({ faqs }) {
  const [open, setOpen] = React.useState(0);
  if (!faqs.length) return null;
  return (
    <div style={{marginTop:'52px'}}>
      <div style={{fontSize:'.68rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.14em',color:'#c0b8a8',marginBottom:'20px',display:'flex',alignItems:'center',gap:'12px'}}>
        Preguntas frecuentes
        <span style={{flex:1,height:'1px',background:'#e8e2d8',display:'block'}}/>
      </div>
      {faqs.map((faq, i) => (
        <div key={i} style={{border:'1px solid #ede9e0',borderRadius:'12px',marginBottom:'8px',overflow:'hidden',background:'white',boxShadow: open===i ? '0 4px 20px rgba(26,23,68,.08)' : 'none',transition:'box-shadow .2s'}}>
          <button onClick={() => setOpen(open === i ? -1 : i)}
            style={{width:'100%',padding:'16px 20px',background:'none',border:'none',textAlign:'left',fontFamily:"'DM Sans',sans-serif",fontSize:'.93rem',fontWeight:600,color:'var(--navy)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px'}}>
            {faq.q}
            <span style={{flexShrink:0,width:'22px',height:'22px',borderRadius:'50%',border:'1.5px solid',borderColor: open===i ? 'var(--navy)':'#e5dfd5',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.8rem',color: open===i ? 'white':'#aaa',background: open===i ? 'var(--navy)':'transparent',transform: open===i ? 'rotate(45deg)':'none',transition:'all .25s',lineHeight:1}}>+</span>
          </button>
          <div style={{maxHeight: open===i ? '400px':'0',overflow:'hidden',transition:'max-height .32s ease'}}>
            <div style={{padding:'0 20px 18px',paddingTop:'14px',fontSize:'.9rem',color:'#555',lineHeight:1.75,borderTop:'1px solid #f0ebe2'}}>
              {faq.a}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Feedback widget ── */
function FeedbackWidget({ examUrl }) {
  const [state, setState] = React.useState('question'); // question | yes | no | thanks
  const [text, setText] = React.useState('');
  if (state === 'yes') return (
    <div style={{background:'white',borderRadius:'18px',padding:'28px 32px',marginTop:'28px',textAlign:'center',border:'1px solid #ede9e0',boxShadow:'0 2px 14px rgba(26,23,68,.06)'}}>
      <p style={{color:'#16a34a',fontWeight:700,marginBottom:'10px',fontSize:'.9rem'}}>✓ ¡Qué bueno que te sirvió!</p>
      <p style={{fontSize:'.85rem',color:'#666',marginBottom:'14px'}}>El siguiente paso es descubrir exactamente en qué nivel estás:</p>
      <Link to={examUrl} className="btn-glow" style={{display:'flex',justifyContent:'center'}}>Hacer mi examen gratis →</Link>
    </div>
  );
  if (state === 'no') return (
    <div style={{background:'white',borderRadius:'18px',padding:'28px 32px',marginTop:'28px',border:'1px solid #ede9e0',boxShadow:'0 2px 14px rgba(26,23,68,.06)'}}>
      <p style={{fontFamily:"'Fraunces',serif",fontSize:'1.05rem',fontWeight:900,color:'var(--navy)',marginBottom:'12px',textAlign:'center'}}>¿Qué te hubiera gustado ver?</p>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Tu feedback nos ayuda a mejorar..."
        style={{width:'100%',padding:'12px 14px',border:'1.5px solid #e5dfd5',borderRadius:'10px',fontFamily:"'DM Sans',sans-serif",fontSize:'.88rem',resize:'vertical',minHeight:'80px',color:'var(--text)',background:'var(--cream)',outline:'none',display:'block',boxSizing:'border-box'}}/>
      <button onClick={() => setState('thanks')}
        style={{marginTop:'10px',background:'var(--navy)',color:'white',border:'none',padding:'9px 20px',borderRadius:'8px',fontFamily:"'DM Sans',sans-serif",fontSize:'.85rem',fontWeight:600,cursor:'pointer'}}>
        Enviar →
      </button>
    </div>
  );
  if (state === 'thanks') return (
    <div style={{background:'white',borderRadius:'18px',padding:'28px 32px',marginTop:'28px',textAlign:'center',border:'1px solid #ede9e0',boxShadow:'0 2px 14px rgba(26,23,68,.06)'}}>
      <p style={{color:'#16a34a',fontWeight:700,fontSize:'.9rem'}}>✓ ¡Gracias por tu feedback!</p>
    </div>
  );
  return (
    <div style={{background:'white',borderRadius:'18px',padding:'28px 32px',marginTop:'28px',textAlign:'center',border:'1px solid #ede9e0',boxShadow:'0 2px 14px rgba(26,23,68,.06)'}}>
      <p style={{fontFamily:"'Fraunces',serif",fontSize:'1.05rem',fontWeight:900,color:'var(--navy)',marginBottom:'16px'}}>¿Este artículo te fue útil?</p>
      <div style={{display:'flex',justifyContent:'center',gap:'10px'}}>
        <button onClick={() => setState('yes')} style={{padding:'10px 24px',borderRadius:'100px',border:'1.5px solid #e5dfd5',background:'white',fontFamily:"'DM Sans',sans-serif",fontSize:'.88rem',fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'6px',color:'#555',transition:'all .22s'}}>👍 Sí, me ayudó</button>
        <button onClick={() => setState('no')}  style={{padding:'10px 24px',borderRadius:'100px',border:'1.5px solid #e5dfd5',background:'white',fontFamily:"'DM Sans',sans-serif",fontSize:'.88rem',fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'6px',color:'#555',transition:'all .22s'}}>🤔 Me faltó algo</button>
      </div>
    </div>
  );
}

function StickyCtaBar({ slug, onExam, onWA, examUrl }) {
  const [visible, setVisible] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const el = document.documentElement;
      const pct = el.scrollHeight - el.clientHeight > 0
        ? (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100 : 0;
      setVisible(pct > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (dismissed || !visible) return null;

  const waMsg = encodeURIComponent('Hola, leí un artículo del blog y me gustaría información sobre los cursos de inglés.');

  return (
    <div className="bp-sticky-cta visible">
      <div className="sticky-row">
        <span style={{color:'rgba(255,255,255,.82)',fontSize:'.85rem',fontWeight:600,lineHeight:1.3}}>
          ¿Listo para mejorar tu inglés?
        </span>
        <button onClick={() => setDismissed(true)}
          style={{background:'none',border:'none',color:'rgba(255,255,255,.3)',fontSize:'1.1rem',cursor:'pointer',padding:'4px',lineHeight:1,flexShrink:0}}>
          ×
        </button>
      </div>
      {/* Mobile: inline row. Desktop: own row via .sticky-btns */}
      <div className="sticky-btns" style={{display:'flex',gap:'8px',flex:1}}>
        <Link to={examUrl}
          className="btn-glow" style={{fontSize:'.8rem',padding:'9px 16px',whiteSpace:'nowrap',flex:1,justifyContent:'center'}}
          onClick={onExam}>
          Examen gratis →
        </Link>
        <a href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`}
          target="_blank" rel="noreferrer"
          className="btn-outline"
          style={{fontSize:'.8rem',padding:'9px 14px',whiteSpace:'nowrap',color:'rgba(255,255,255,.8)',border:'1px solid rgba(255,255,255,.2)'}}
          onClick={onWA}>
          💬
        </a>
      </div>
    </div>
  );
}

/* ── Exit Intent Popup ── */
function ExitIntentPopup({ examUrl, show, onClose, category }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copies = {
    'Certificaciones': { emoji: '🎓', title: 'Tu certificación empieza\ncon saber tu nivel', sub: 'El examen es gratis y tarda 10 minutos. Sin registro.' },
    'default':         { emoji: '⏳', title: 'Espera — ¿ya sabes tu\nnivel de inglés?',    sub: 'El examen es gratis, tarda 10 minutos y no necesitas registrarte.' },
  };
  const c = copies[category] || copies['default'];

  if (!show) return null;
  return (
    <div className="ei-overlay show" onClick={e => e.target.classList.contains('ei-overlay') && onClose()}>
      <div className="ei-modal">
        <div className="ei-top">
          <button className="ei-close" onClick={onClose}>×</button>
          <div style={{fontSize:'2.6rem',marginBottom:'12px',position:'relative',zIndex:1}}>{c.emoji}</div>
          <h2 className="ei-title">{c.title.split('\n').map((t,i) => <React.Fragment key={i}>{t}{i===0&&<br/>}</React.Fragment>)}</h2>
          <p className="ei-sub">{c.sub}</p>
        </div>
        <div className="ei-bottom">
          <Link to={examUrl} className="btn-glow" style={{justifyContent:'center'}} onClick={onClose}>
            Hacer mi examen gratis →
          </Link>
          <a href="https://wa.me/${WA_NUMBER}?text=${WA_MSG}"
            target="_blank" rel="noreferrer"
            className="btn-outline" style={{justifyContent:'center',background:'rgba(26,23,68,.05)',color:'var(--navy)',border:'1.5px solid rgba(26,23,68,.15)'}}
            onClick={onClose}>
            💬 Hablar con un asesor
          </a>
          <button className="ei-dismiss" onClick={onClose}>No gracias, ya sé mi nivel</button>
        </div>
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug }                = useParams();
  const isPreview               = new URLSearchParams(window.location.search).get('preview') === '1';
  const [post, setPost]         = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [abVariant, setAbVariant]       = useState('A');
  const [exitIntentShow, setExitIntentShow] = useState(false);

  const scrollMaxRef   = React.useRef(0);
  const timeActiveRef  = React.useRef(0);
  const ctaClicksRef   = React.useRef(0);
  const waClickRef     = React.useRef(0);
  const waLeadRef      = React.useRef(0);
  const sessionIdRef   = React.useRef(getSessionId());
  const engageSentRef  = React.useRef(false);

  /* ── 1. Fetch post + fire view event ── */
  useEffect(() => {
    setLoading(true); setNotFound(false); setPost(null); setRelated([]);
    scrollMaxRef.current = 0; timeActiveRef.current = 0;
    ctaClicksRef.current = 0; waClickRef.current = 0;
    waLeadRef.current = 0; engageSentRef.current = false;

    fetch(`${API}?action=post&slug=${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(data => {
        setPost(data); setLoading(false);

        // ── Guard: only fire view once per slug per session ──
        const viewKey = `cl_view_${slug}`;
        const alreadySent = sessionStorage.getItem(viewKey);
        if (!alreadySent && !isPreview) {
          sessionStorage.setItem(viewKey, '1');
          const ref = document.referrer;
          const isInternal = !!(ref && (ref.includes('culturlingua.com') || ref.includes('localhost')));
          const variant = getABVariant();
          setAbVariant(variant);
          fetch(`${API}?action=view&slug=${slug}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              is_internal: isInternal,
              referrer: ref || '',
              device: getDevice(),
              session_id: sessionIdRef.current,
              ab_variant: variant,
              ...getUTMs()
            }),
          });
        } else {
          // Already counted — still set AB variant for UI consistency
          setAbVariant(getABVariant());
        }

        return fetch(`${API}?action=posts&category=${encodeURIComponent(data.category)}`);
      })
      .then(r => r.json())
      .then(all => setRelated((Array.isArray(all) ? all : []).filter(p => p.slug !== slug).slice(0, 2)))
      .catch(() => { setLoading(false); setNotFound(true); });
  }, [slug]);

  /* ── 2. Scroll depth ── */
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const pct = el.scrollHeight - el.clientHeight > 0 ? Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100) : 0;
      if (pct > scrollMaxRef.current) scrollMaxRef.current = pct;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [slug]);

  /* ── 2b. Heatmap click capture on article ── */
  useEffect(() => {
    if (!post) return;
    const article = document.querySelector('.blog-content');
    if (!article) return;
    article.addEventListener('click', recordHeatmapClick);
    return () => article.removeEventListener('click', recordHeatmapClick);
  }, [post, slug]);

  /* ── 3. Active time (pauses when tab hidden) ── */
  useEffect(() => {
    if (!post) return;
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(() => { timeActiveRef.current += 1; }, 1000); };
    const stop  = () => { clearInterval(interval); interval = null; };
    const onVis = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [post, slug]);

  /* ── 4. Flush on leave / slug change ── */
  const flushEngage = React.useCallback((targetSlug) => {
    if (engageSentRef.current) return;
    if (isPreview) return;
    engageSentRef.current = true;
    const bounced = scrollMaxRef.current < 10 && timeActiveRef.current < 10;
    const payload = JSON.stringify({
      session_id: sessionIdRef.current,
      scroll_max: scrollMaxRef.current,
      time_active: timeActiveRef.current,
      cta_clicks: ctaClicksRef.current,
      wa_click: waClickRef.current > 0,
      wa_lead: waLeadRef.current > 0,
      bounced
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}?action=engage&slug=${targetSlug}`, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(`${API}?action=engage&slug=${targetSlug}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true });
    }
  }, []);

  useEffect(() => {
    if (!post) return;
    const captured = slug;
    const onUnload = () => flushEngage(captured);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => { flushEngage(captured); window.removeEventListener('pagehide', onUnload); window.removeEventListener('beforeunload', onUnload); };
  }, [post, slug, flushEngage]);

  /* ── 5. Exit intent — mouseleave top edge (desktop) ── */
  useEffect(() => {
    if (!post) return;
    let fired = false;
    let activeTime = 0;
    const timer = setInterval(() => { activeTime++; }, 1000);
    const onLeave = (e) => {
      if (fired || e.clientY > 8 || activeTime < 5) return;
      fired = true;
      setExitIntentShow(true);
    };
    document.addEventListener('mouseleave', onLeave);
    return () => {
      document.removeEventListener('mouseleave', onLeave);
      clearInterval(timer);
    };
  }, [post, slug]);

  if (loading) return (
    <div className="bp-fullscreen">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{textAlign:'center'}}>
        <div style={{display:'inline-block',width:'36px',height:'36px',
          border:'3px solid #e5e7eb',borderTopColor:'var(--blue)',
          borderRadius:'50%',animation:'spin .8s linear infinite'}}/>
      </div>
    </div>
  );

  if (notFound || (!post && !loading)) return (
    <div className="bp-fullscreen" style={{flexDirection:'column',gap:'16px',textAlign:'center',padding:'48px'}}>
      <div style={{fontSize:'3rem'}}>📭</div>
      <h2 style={{fontFamily:"'Fraunces',serif",color:'var(--navy)'}}>Artículo no encontrado</h2>
      <p style={{color:'#888'}}>Este artículo no existe o fue eliminado.</p>
      <Link to="/blog" style={{color:'var(--blue)',fontWeight:700}}>← Ver todos los artículos</Link>
    </div>
  );

  const { c1, c2 } = HeroVisual({
    color: post.category_color,
    category: post.category,
  });

  const shareUrl = `https://culturlingua.com/blog/${post.slug}`;

  const faqs      = parseFaqs(post.faq_json);
  const faqSchema = buildFaqSchema(faqs, shareUrl);
  // Pass session_id to exam so funnel can be closed server-side
  const examBase  = '/Examen-de-ubicacion';
  const examParams = new URLSearchParams({
    from:       'blog',
    slug:       post.slug,
    sid:        sessionIdRef.current,
  }).toString();
  const examUrl = `${examBase}?${examParams}`;

  // OG image: prefer post-specific, fallback to logo
  const ogImage = post.og_image
    ? post.og_image
    : 'https://culturlingua.com/logo.webp';

  return (
    <div className="page-wrapper">
      <style>{S}</style>
      {isPreview && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:9999,
          background:'#f59e0b', color:'#1a1744',
          padding:'8px 20px', textAlign:'center',
          fontSize:'0.82rem', fontWeight:700,
          display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
          boxShadow:'0 2px 8px rgba(0,0,0,0.15)'
        }}>
          👁 MODO PREVIEW — ningún dato se registra en analytics
          <span style={{opacity:0.6, fontWeight:400}}>
            Quita ?preview=1 de la URL para ver como visitante real
          </span>
        </div>
      )}
      {isPreview && <div style={{height:'38px'}}/>}
      <ReadingProgress />

      <Helmet key={post.slug}>
        <title>{post.title} | Culturlingua</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="es_MX" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:modified_time" content={post.updated_at || post.created_at} />
        <meta property="article:author" content={SITE_AUTHOR.name} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BlogPosting",
          "mainEntityOfPage":{"@type":"WebPage","@id":shareUrl},
          "headline":        post.title,
          "description":     post.excerpt,
          "url":             shareUrl,
          "datePublished":   post.created_at,
          "dateModified":    post.updated_at || post.created_at,
          "wordCount":       post.word_count || undefined,
          "articleSection":  post.category,
          "image":           ogImage,
          "author":{"@type":"Organization","name":SITE_AUTHOR.name,"url":"https://culturlingua.com"},
          "publisher":{"@type":"Organization","name":"Culturlingua",
            "logo":{"@type":"ImageObject","url":"https://culturlingua.com/logo.webp"}},
          "breadcrumb":{"@type":"BreadcrumbList","itemListElement":[
            {"@type":"ListItem","position":1,"name":"Inicio","item":"https://culturlingua.com"},
            {"@type":"ListItem","position":2,"name":"Blog","item":"https://culturlingua.com/blog"},
            {"@type":"ListItem","position":3,"name":post.title,"item":shareUrl},
          ]},
        })}</script>
        {faqSchema && <script type="application/ld+json">{faqSchema}</script>}
      </Helmet>

      {/* ── Hero (colored header) ── */}
      <div className="bp-hero" style={{ background:'var(--navy)' }}>
        {/* Subtle dot grid — same as rest of site */}
        <div style={{position:'absolute',inset:0,
          backgroundImage:'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize:'28px 28px', pointerEvents:'none'}}/>
        {/* Single soft orb top-right */}
        <div style={{position:'absolute',top:'-60px',right:'-60px',width:'280px',height:'280px',
          background:'radial-gradient(circle,rgba(245,200,66,.09) 0%,transparent 70%)',pointerEvents:'none'}}/>
        {/* Emoji — clean, no blur, top-right */}
        {post.icon && (
          <div style={{position:'absolute',top:'36px',right:'52px',fontSize:'64px',
            opacity:0.18,userSelect:'none',pointerEvents:'none',lineHeight:1,
          }}>{post.icon}</div>
        )}

        <div className="bp-hero-inner">
          {/* Breadcrumb */}
          <div className="bp-breadcrumb">
            <Link to="/blog" className="bp-back">← Blog</Link>
            <span className="bp-sep">/</span>
            <span className="bp-cat-pill" style={{
              background:`${c1}28`, color:c1,
              border:`1px solid ${c1}44`,
            }}>{post.category}</span>
          </div>

          <h1 className="bp-title">{post.title}</h1>
          <p className="bp-excerpt">{post.excerpt}</p>

          <div className="bp-meta">
            <span className="bp-meta-item">📅 {formatDate(post.created_at)}</span>
            <span className="bp-meta-dot">●</span>
            <span className="bp-meta-item">⏱ {post.read_time}</span>
            {post.views > 100 && (
  <span className="bp-meta-item">
    👁️ {post.views.toLocaleString()} lecturas
  </span>
)}
            <span className="bp-meta-dot">●</span>
            <span className="bp-meta-item">✍️ Culturlingua</span>
          </div>
        </div>

        {/* Category color accent strip at bottom of hero */}
        <div style={{
          position:'absolute', bottom:0, left:0, right:0, height:'4px',
          background:`linear-gradient(90deg, ${c1}, ${c2})`,
        }}/>
        {/* Wave transition */}
        <svg className="bp-wave" viewBox="0 0 1440 48" preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M0,48 C360,0 1080,48 1440,16 L1440,48 Z" fill="var(--cream)"/>
        </svg>
      </div>

      {/* ── Article body ── */}
      <div className="bp-body">
        <div className="bp-body-inner">

          {/* Table of Contents */}
          <TableOfContents content={post.content} readTime={post.read_time} />

          {/* Share strip */}
          <div className="bp-share">
            <span className="bp-share-lbl">Compartir</span>
            <a className="bp-share-btn bp-share-wa" target="_blank" rel="noreferrer"
              href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + shareUrl)}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L.057 23.492a.5.5 0 00.624.606l5.807-1.523A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.7-.504-5.25-1.385l-.373-.215-3.873 1.016 1.032-3.768-.235-.385A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              WhatsApp
            </a>
            <a className="bp-share-btn bp-share-fb" target="_blank" rel="noreferrer"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
              Facebook
            </a>
            <a className="bp-share-btn bp-share-li" target="_blank" rel="noreferrer"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            <a className="bp-share-btn" target="_blank" rel="noreferrer"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </a>
            <a className="bp-share-btn bp-share-mail"
              href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent('Te comparto este artículo: ' + shareUrl)}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>
              Email
            </a>
          </div>

          {/* Content — split at ~40% to inject inline CTA */}
          {(() => {
            if (!post.content) return null;
            // Find split point: after the 2nd <h2> or at ~40% char index, whichever is earlier
            const content = post.content;
            const h2matches = [...content.matchAll(/<h2[\s>]/gi)];
            let splitIdx = Math.floor(content.length * 0.42);
            if (h2matches.length >= 2) {
              // Split after the 2nd h2 closes — find the </h2> after the 2nd match
              const after2nd = content.indexOf('</h2>', h2matches[1].index);
              if (after2nd > 0) splitIdx = after2nd + 5;
            }
            const part1 = content.slice(0, splitIdx);
            const part2 = content.slice(splitIdx);
            return (
              <>
                <article className="blog-content" dangerouslySetInnerHTML={{ __html: part1 }} />
                <InlineCta examUrl={examUrl} category={post.category} />
                <article className="blog-content" dangerouslySetInnerHTML={{ __html: part2 }} />
              </>
            );
          })()}

          {/* Author bio */}
          <AuthorBio />

          {/* Feedback widget */}
          <FeedbackWidget examUrl={examUrl} />

          {/* FAQ accordion — only if post has FAQs */}
          <FaqAccordion faqs={faqs} />

          {/* CTA */}
          <div className="bp-cta">
            {/* Top — navy dark section */}
            <div className="bp-cta-top">
              <div className="bp-cta-orb"/><div className="bp-cta-orb2"/>
              <div style={{position:'relative',zIndex:2}}>
                <div className="bp-cta-badge">
                  <span>✦</span> Prueba gratuita
                </div>
                {abVariant === 'A' ? (
                  <>
                    <h3>Descubre tu nivel de inglés<br/>en 10 minutos — gratis</h3>
                    <p className="bp-cta-sub">
                      Sin registro, sin tarjeta. Solo un examen rápido y recibes
                      una recomendación personalizada de curso.
                    </p>
                  </>
                ) : (
                  <>
                    <h3>¿Sabes en qué nivel estás?<br/>Descúbrelo ahora — es gratis</h3>
                    <p className="bp-cta-sub">
                      Miles de estudiantes en Guadalajara ya conocen su nivel.
                      Toma el examen en 10 minutos y recibe tu plan personalizado.
                    </p>
                  </>
                )}
              </div>
            </div>
            {/* Bottom — cream section with perks + buttons */}
            <div className="bp-cta-bottom">
              <div className="bp-cta-perks">
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Grupos pequeños (max 8)
                </span>
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Maestros certificados
                </span>
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#22c55e" opacity=".15"/><path d="M4 6.5l1.8 1.8L9 4.5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Tlaquepaque, Jalisco
                </span>
                <span className="bp-cta-perk">
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6.5" fill="#f5c842" opacity=".25"/><path d="M6.5 2l1.1 2.2 2.4.35-1.75 1.7.41 2.4L6.5 7.5 4.34 8.65l.41-2.4L3 4.55l2.4-.35z" fill="#c9a000"/></svg>
                  Lugares disponibles esta semana
                </span>
              </div>
              <div className="bp-cta-btns">
                <Link to={examUrl} className="btn-glow"
                  onClick={() => { ctaClicksRef.current += 1; }}>
                  Hacer mi examen gratis →
                </Link>
                <a className="btn-outline"
                  href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`}
                  target="_blank" rel="noreferrer"
                  onClick={() => {
                    ctaClicksRef.current += 1;
                    waClickRef.current += 1;
                    // Mark as lead after 3s (they opened WA) — rough heuristic
                    setTimeout(() => { waLeadRef.current = 1; }, 3000);
                  }}>
                  💬 Hablar con un asesor
                </a>
              </div>
              <div className="bp-cta-social">
                <div className="bp-cta-social-line">
                  <span className="bp-cta-avatars">
                    {['MR','LG','AP','KF'].map((i,idx) => (
                      <span key={idx} className="bp-cta-avatar"
                        style={{background:['#1a1744','#3b6fe0','#f5c842','#22c55e'][idx],
                          color:idx===2?'#1a1744':'white'}}>
                        {i}
                      </span>
                    ))}
                  </span>
                  Más de 500 alumnos han avanzado con Culturlingua
                </div>
              </div>
            </div>
          </div>

          {/* ── Sticky CTA bar ── */}
          <style>{`
            @keyframes slideUp { from { transform:translateY(100%); opacity:0; } to { transform:translateY(0); opacity:1; } }
            .bp-sticky-cta { display:none; position:fixed; bottom:0; left:0; right:0; z-index:200; background:var(--navy); padding:12px 20px; box-shadow:0 -4px 24px rgba(26,23,68,.25); animation:slideUp .35s ease both; align-items:center; justify-content:space-between; gap:10px; border-top:1px solid rgba(255,255,255,.08); }
            .bp-sticky-cta.visible { display:flex; }
            @media(min-width:860px){
              .bp-sticky-cta { left:auto; right:24px; bottom:24px; border-radius:16px; border-top:none; width:300px; flex-direction:column; align-items:flex-start; box-shadow:0 8px 40px rgba(26,23,68,.35); padding:16px 18px; }
              .bp-sticky-cta .sticky-row { display:flex; width:100%; align-items:center; justify-content:space-between; }
              .bp-sticky-cta .sticky-btns { display:flex; gap:8px; width:100%; margin-top:10px; }
              .bp-sticky-cta .sticky-btns a { flex:1; text-align:center; justify-content:center; }
            }
          `}</style>
          <StickyCtaBar
            slug={slug}
            examUrl={examUrl}
            onExam={() => { ctaClicksRef.current += 1; }}
            onWA={() => {
              ctaClicksRef.current += 1;
              waClickRef.current += 1;
              setTimeout(() => { waLeadRef.current = 1; }, 3000);
            }}
          />

          {/* Related */}
          {related.length > 0 && (
            <div className="bp-related">
              <div className="bp-rel-lbl">Artículos relacionados</div>
              <div className="bp-rel-grid">
                {related.map(r => (
                  <Link key={r.slug} to={'/blog/' + r.slug} className="bp-rel-card">
                    <RelVisual color={r.category_color} icon={r.icon} category={r.category}/>
                    <div className="bp-rel-body">
                      <div className="bp-rel-title">{r.title}</div>
                      <span className="bp-rel-cta">Leer <span>→</span></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Exit intent popup */}
      <ExitIntentPopup
        examUrl={examUrl}
        show={exitIntentShow}
        onClose={() => setExitIntentShow(false)}
        category={post?.category}
      />
    </div>
  );
}